// A WEBSITE'S PAGES IN AN AI ANSWER — the `project-pages` view (functions/
// previewAnswer.js previewAiViews, functions/previewPagesAnswer.js; the shell
// mounts it in the assistant's thread and on a Fly page with scope
// { params, embedded }). Read-only: the site's pages — or its components — as
// screenshot tiles (Pages · Components switch the set); a tile swaps in the
// live thing (the published page, or the component in the sandboxed
// user-code frame) with a way back. While reading: tile stand-ins; no such
// site, or no website at all: one sentence (previewAnswerEmptyText) — a name
// that is no website says what it is, with its name.
export const ProjectPagesView = {
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  height: '100%',
  minHeight: '0',
  dataProjectPages: true,
  state: {
    site: null,
    failed: false,
    none: false,
    // The asked name when it is no website (functions/previewAnswer.js
    // previewAnswerResolve): a project of this workspace, or an App Store app.
    notWebsite: null,
    storeApp: null,
    show: null,
    tiles: null,
    total: 0,
    published: null,
    serves: null,
    unread: false,
    live: null,
    liveReady: false,
    liveError: null,
    frameReady: null,
    frameSlow: null,
    boxW: 0,
    boxH: 0
  },
  // EMPTY (the views contract, workspace functions/assistantAppViews.js): no
  // such site, or no website at all — the one sentence is all the view shows.
  // A site with no pages keeps its head (the Pages · Components switch).
  empty: (el, s) => (s.failed ? true : s.site ? false : null),
  // RESOLVED PARAMS (the views contract): the site and environment the card
  // SHOWS — a follow-up stays on this site. null while reading.
  resolvedParams: (el, s) => (s.site ? { project: s.site.key, env: s.site.env } : null),
  onRender: (el) => el.call('previewPagesLoad', el),
  // The site card's own inputs (SitePreviewView): the App Store list too —
  // a late list can change what the asked name means (canvasAnswerNamedMoved).
  subscribeTo: ['workspaceApps', '_projectsForActiveOrg', '_previewParams', '_wsAppsCatalog'],
  onUpdate: (el) => el.call('previewPagesLoad', el),

  ProjectPagesSkeleton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-E2)), 1fr))',
    gap: 'Z',
    ariaBusy: 'true',
    hide: (el, s) => Array.isArray(s.tiles) || !!s.failed,
    Skeleton_1: { aspectRatio: '16 / 10', round: 'radiusControl' },
    Skeleton_2: { aspectRatio: '16 / 10', round: 'radiusControl' },
    Skeleton_3: { aspectRatio: '16 / 10', round: 'radiusControl' }
  },

  ProjectPagesNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !s.failed && !(Array.isArray(s.tiles) && !s.tiles.length),
    text: (el, s) => {
      void s.root.lang
      if (s.failed) return el.call('previewAnswerEmptyText', s)
      const key = s.unread
        ? 'preview.pagesView.unread'
        : s.show === 'components'
          ? 'preview.pagesView.noComponents'
          : 'preview.pagesView.noPages'
      return String(el.call('polyglot', key)).replace('{name}', () => (s.site ? s.site.name : ''))
    }
  },

  ProjectPagesHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !s.site,
    ProjectPagesName: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        void s.root.lang
        const site = s.site
        if (!site) return ''
        const env = String(el.call('polyglot', el.call('previewEnvLabel', site.env)))
        return s.live ? site.name + ' · ' + s.live.title : site.name + ' · ' + env
      }
    },
    // Pages · Components — the set the tiles show.
    AppSegmented: {
      compact: true,
      hide: (el, s) => !!s.live,
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.pagesView.show')
      },
      children: () => [
        { key: 'opt-pages', optionValue: 'pages', optionLabel: 'preview.sidebar.category.pages' },
        {
          key: 'opt-components',
          optionValue: 'components',
          optionLabel: 'preview.sidebar.category.components'
        }
      ],
      childProps: {
        isActive: (el, s) => (s.show || 'pages') === el.optionValue,
        ariaPressed: (el, s) => ((s.show || 'pages') === el.optionValue ? 'true' : 'false'),
        onClick: (e, el, s) => {
          e.stopPropagation()
          if ((s.show || 'pages') === el.optionValue) return
          s.update({ show: el.optionValue })
        }
      }
    },
    CompactIconButton: {
      icon: 'arrowLeft',
      hide: (el, s) => !s.live,
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.pagesView.back')
      },
      title: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.pagesView.back')
      },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('previewPagesBack', el)
      }
    }
  },

  ProjectPagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-E2)), 1fr))',
    gap: 'Z',
    minWidth: '0',
    minHeight: '0',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    hide: (el, s) => !!s.live || !Array.isArray(s.tiles) || !s.tiles.length,
    childExtends: 'ProjectPagesTile',
    childrenAs: 'state',
    children: (el, s) => (Array.isArray(s.tiles) ? s.tiles : [])
  },

  // The first N of more: says so, never a silent cut.
  ProjectPagesMore: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !!s.live || !Array.isArray(s.tiles) || !(s.total > s.tiles.length),
    text: (el, s) => {
      void s.root.lang
      return String(el.call('polyglot', 'preview.pagesView.more'))
        .replace('{n}', String(Array.isArray(s.tiles) ? s.tiles.length : 0))
        .replace('{total}', String(s.total || 0))
    }
  },

  // The live thing: the published page (its address, at a desktop's width,
  // scaled to the card) or the component in the user-code frame.
  ProjectPagesLive: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    aspectRatio: '16 / 10',
    flex: '0 1 auto',
    minHeight: '0',
    round: 'radiusControl',
    background: 'overlay',
    hide: (el, s) => !s.live,
    stateDeps: [(el, s) => !!s.live],
    onStateUpdate: (el) => el.call('previewAnswerMeasure', el),
    onWindowResize: (e, el) => el.call('previewAnswerMeasure', el),
    ProjectPagesPageFrame: {
      position: 'absolute',
      inset: '0',
      hide: (el, s) => !s.live || s.live.kind !== 'page',
      children: (el, s) => {
        const live = s.live
        if (!live || live.kind !== 'page' || !live.url) return []
        const frameKey = live.url + '#0'
        const id = Array.from(frameKey)
          .reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7)
          .toString(36)
        return [
          {
            extends: 'Iframe',
            key: 'page-' + id,
            frameKey,
            src: live.url,
            title: String(el.call('polyglot', 'preview.answer.frameTitle')).replace('{name}', () =>
              s.site ? s.site.name + ' · ' + live.title : live.title
            ),
            sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
            referrerpolicy: 'no-referrer',
            display: 'block',
            position: 'absolute',
            top: '0',
            transformOrigin: '0 0',
            border: 'none',
            style: (el, s) => el.call('previewAnswerFrameStyle', s),
            onLoad: (e, el) => el.call('previewAnswerFrameLoaded', el),
            onRender: (el) => el.call('previewAnswerFrameTimer', el),
            onBeforeRemove: (el) => clearTimeout(el.__previewSlowTimer)
          }
        ]
      }
    },
    ProjectPagesComponentFrame: {
      tag: 'iframe',
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      border: 'none',
      display: 'block',
      background: 'white',
      '@dark': { background: 'black' },
      hide: (el, s) => !s.live || s.live.kind !== 'component',
      title: (el, s) => (s.live ? s.live.title : ''),
      sandbox: (el) => el.call('userCodeSandbox')
    },
    ProjectPagesLiveNote: {
      tag: 'p',
      role: 'status',
      position: 'absolute',
      left: 'Z',
      right: 'Z',
      bottom: 'Z',
      margin: '0',
      padding: 'Y Z',
      round: 'radiusControl',
      theme: 'dialog',
      fontSize: 'Z',
      textAlign: 'center',
      pointerEvents: 'none',
      hide: (el, s) => !s.live || !el.call('previewPagesLiveNote', s),
      text: (el, s) => {
        void s.root.lang
        return el.call('previewPagesLiveNote', s)
      }
    }
  }
}

// One tile: the capture of a page or a component (a neutral plate when the
// CDN has none — the image's own `alt` names it), its address or name under
// it. A button: it opens the live thing in the card.
export const ProjectPagesTile = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: 'X',
  minWidth: '0',
  padding: 'X',
  border: 'none',
  background: 'transparent',
  color: 'title',
  textAlign: 'left',
  cursor: 'pointer',
  round: 'radiusControl',
  transition: 'background-color A defaultBezier',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  ariaLabel: (el, s) => {
    void s.root.lang
    return String(el.call('polyglot', 'preview.pagesView.openTile')).replace(
      '{name}',
      () => s.title || ''
    )
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('previewPagesOpen', el, s.parse ? s.parse() : s)
  },
  ProjectPagesTileShot: {
    width: '100%',
    aspectRatio: '16 / 10',
    round: 'radiusControl',
    overflow: 'hidden',
    background: 'overlay',
    Img: {
      src: (el, s) => s.image || null,
      alt: (el, s) => s.title || '',
      loading: 'lazy',
      boxSize: '100%',
      objectFit: 'cover',
      display: 'block',
      hide: (el, s) => !s.image,
      // No capture on the CDN for this page: the plate, never a broken image.
      onError: (e, el, s) => s.update({ image: null })
    }
  },
  ProjectPagesTileTitle: {
    tag: 'span',
    minWidth: '0',
    fontSize: 'Z',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.title || ''
  }
}
